/**
 * Calls for the Fisheye dewarp tab (ENG-2761). Staff only on admin's side:
 * `camera/{id}/panorama/` answers 403 to anyone else.
 *
 *  GET   camera/{id}/panorama/  → CameraPanorama
 *  PATCH camera/{id}/panorama/  {panorama_parameters} ('' clears) → CameraPanorama,
 *        400 {panorama_parameters: ["message"]} when the flags are invalid.
 *
 * The raw frame comes from the site's latest camera test (dry run), whose
 * per-camera snapshot is the un-dewarped fisheye.
 */
import { AxiosError } from "axios";
import { createApi } from "../../../utils/createApi";
import { withSessionRecovery } from "./authRecovery";
import { fetchLatestCameraDryRun } from "../../Sites/SiteConnectivity/siteConnectivityApi";

export interface CameraPanorama {
  camera_id: number;
  camera_name: string;
  site_id: number | null;
  panorama_parameters: string | null;
  /** Not applied by the connector at all; shown so staff can move it. */
  dewarp_parameters: string | null;
  camera_view: string | null;
}

export type PanoramaOutcome =
  | { kind: "ok"; data: CameraPanorama }
  | { kind: "unavailable"; endpoint: string }
  | { kind: "forbidden"; message: string }
  | { kind: "invalid"; message: string }
  | { kind: "error"; message: string };

export const panoramaPath = (cameraId: number) =>
  `camera/${cameraId}/panorama/`;

const firstMessage = (value: unknown): string | null => {
  if (typeof value === "string" && value) return value;
  if (Array.isArray(value) && typeof value[0] === "string") return value[0];
  return null;
};

const outcomeFromError = (
  error: unknown,
  cameraId: number
): PanoramaOutcome => {
  const response = (error as AxiosError)?.response;
  const status = response?.status;
  const body = (response?.data ?? {}) as Record<string, unknown>;
  if (status === 404 || status === 405)
    return { kind: "unavailable", endpoint: panoramaPath(cameraId) };
  if (status === 401 || status === 403)
    return {
      kind: "forbidden",
      message:
        firstMessage(body.detail) ??
        "Only Actuate staff can read or change panorama parameters.",
    };
  if (status === 400)
    return {
      kind: "invalid",
      message:
        firstMessage(body.panorama_parameters) ??
        firstMessage(body.detail) ??
        "The server rejected these panorama parameters.",
    };
  if (status != null && status >= 500)
    return {
      kind: "error",
      message: `The server couldn't answer (error ${status}). Try again in a minute.`,
    };
  return {
    kind: "error",
    message: "We couldn't reach the server. Try again in a moment.",
  };
};

export const fetchCameraPanorama = async (
  cameraId: number
): Promise<PanoramaOutcome> => {
  try {
    const response = await withSessionRecovery(() =>
      createApi("").get<CameraPanorama>(panoramaPath(cameraId))
    );
    return { kind: "ok", data: response.data };
  } catch (error) {
    return outcomeFromError(error, cameraId);
  }
};

/** Save the flags ('' clears them). */
export const saveCameraPanorama = async (
  cameraId: number,
  panoramaParameters: string
): Promise<PanoramaOutcome> => {
  try {
    const response = await withSessionRecovery(() =>
      createApi("").patch<CameraPanorama>(panoramaPath(cameraId), {
        panorama_parameters: panoramaParameters,
      })
    );
    return { kind: "ok", data: response.data };
  } catch (error) {
    return outcomeFromError(error, cameraId);
  }
};

export interface CameraSnapshot {
  url: string;
  /** When the camera test ran. */
  checkedAt: string | null;
  /** When the presigned link stops working. */
  expiresAt: string | null;
}

export type SnapshotOutcome =
  | { kind: "ok"; snapshot: CameraSnapshot }
  /** No test, or the test has no frame for this camera. */
  | { kind: "none" }
  | { kind: "error"; message: string };

/**
 * The camera's frame from the site's latest camera test
 * (`camera-dry-run/latest/?site_id=`), if that test captured one.
 */
export const fetchCameraSnapshot = async (
  siteId: number,
  cameraId: number
): Promise<SnapshotOutcome> => {
  const outcome = await fetchLatestCameraDryRun(siteId);
  if (outcome.kind === "ok") {
    const camera = (outcome.data.cameras ?? []).find(
      (row) => Number(row.camera_id) === cameraId
    );
    if (!camera?.snapshot_url) return { kind: "none" };
    return {
      kind: "ok",
      snapshot: {
        url: camera.snapshot_url,
        checkedAt: outcome.data.checked_at ?? null,
        expiresAt: camera.media_expires_at ?? null,
      },
    };
  }
  if (outcome.kind === "not_found" || outcome.kind === "unavailable")
    return { kind: "none" };
  return {
    kind: "error",
    message:
      "message" in outcome && outcome.message
        ? outcome.message
        : "The latest camera test could not be loaded.",
  };
};

/** Sample preview: a camera with a split band and a stray dewarp value. */
export const SAMPLE_CAMERA_ID = -1;
export const SAMPLE_PANORAMA: CameraPanorama = {
  camera_id: SAMPLE_CAMERA_ID,
  camera_name: "Sample fisheye camera",
  site_id: null,
  panorama_parameters: "-w 3000 -x 180 -la -60 -10 -2",
  dewarp_parameters: "-w 3000 -x 180 -la -80 -10 -2",
  camera_view: "fisheye",
};
